// noinspection JSUnusedGlobalSymbols

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { from, of } from 'rxjs';
import {
  AuthService,
  DownloadService,
  LegalEntityService,
  OrganizationsService,
  TemplateService,
  UsersService,
} from '@pt/api-client';
import * as PtCoreActions from '../actions/pt-core.actions';
import { setQueryParams } from '../actions/pt-core.actions';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as PtCoreSelectors from '../selectors/pt-core.selectors';
import { JobStatus, StorageInfo } from '@primatoo-ms-organization';
import {
  GetLoginModulesResponse,
  LoggedUser,
  NotificationType,
  SessionInfoType,
  SessionNotification,
  SessionNotificationStatus,
  TemplateDocumentType,
  UiPropagatedNotification,
} from '@pt/pt-models';
import { TwoFactorAuthenticationDialogComponent } from '../../components/two-factor-authentication-dialog';
import { LegalEntity } from '@ms-organization-entities';
import { ListTemplateChaptersForDownload } from 'it/primatoo/openapi/api/org/schemas/template/ListTemplateChaptersForDownload';
import { SelectChapterDialogComponent } from '../../dialog';
import { Chapter } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listtemplatechapterresponse/Chapter';
import { SubmitDownloadRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/SubmitDownloadRequest';
import { SubmitDownloadResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/SubmitDownloadResponse';
import { ListTemplateChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/ListTemplateChapterResponse';
import { UpdateOrganizationRequest } from 'it/primatoo/openapi/api/org/schemas/organization/UpdateOrganizationRequest';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';
import { LocalStorageService } from '../../services/local-storage.service';

@Injectable()
export class PtCoreEffects {
  private completedLabel = '';
  private failedLabel = '';
  private ofLabel = '';
  private andLabel = '';

  constructor(
    private actions$: Actions,
    private translate: TranslateService,
    private router: Router,
    private authService: AuthService,
    private organizationsService: OrganizationsService,
    private usersService: UsersService,
    private activatedRoute: ActivatedRoute,
    private downloadService: DownloadService,
    private legalEntityService: LegalEntityService,
    private templateService: TemplateService,
    private modalService: NgbModal,
    private localStorageService: LocalStorageService,
    private store: Store
  ) {
    this.translate
      .get('LABELS.COMPLETED')
      .subscribe((label) => (this.completedLabel = label));
    this.translate
      .get('LABELS.FAILED')
      .subscribe((label) => (this.failedLabel = label));
    this.translate
      .get('LABELS.AND')
      .subscribe((label) => (this.andLabel = label));
    this.translate
      .get('LABELS.OF')
      .subscribe((label) => (this.ofLabel = label));
  }

  cleanData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PtCoreActions.cleanData),
        tap(() => {
          this.localStorageService.cleanStorage(false);
        })
      ),
    { dispatch: false }
  );

  logoutAndClearDataForInactivityTime$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PtCoreActions.logoutAndClearDataForInactivityTime),
        tap((action) => {
          localStorage.clear();
          this.modalService.dismissAll();

          this.localStorageService.cleanStorage(false);
          this.localStorageService.setLatestPage(action.url);
          this.authService.logout(action.domain).subscribe(
            () => {
              this.store.dispatch(
                PtCoreActions.navigateTo({ route: action.route })
              );
            },
            () => {
              this.store.dispatch(
                PtCoreActions.navigateTo({ route: action.route })
              );
            }
          );
          this.store.dispatch(
            PtCoreActions.navigateTo({ route: action.route })
          );
        })
      ),
    { dispatch: false }
  );

  logoutAndCleanData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PtCoreActions.logoutAndClearData),
        tap((action) => {
          this.localStorageService.cleanStorage(false);
          this.modalService.dismissAll();
          this.authService.logout(action.domain).subscribe(
            () => {
              this.store.dispatch(
                PtCoreActions.navigateTo({
                  route: action.route,
                  queryParams: action.queryParams,
                })
              );
            },
            () => {
              this.store.dispatch(
                PtCoreActions.navigateTo({
                  route: action.route,
                  queryParams: action.queryParams,
                })
              );
            }
          );
          this.store.dispatch(
            PtCoreActions.navigateTo({
              route: action.route,
              queryParams: action.queryParams,
            })
          );
        })
      ),
    { dispatch: false }
  );

  navigateTo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PtCoreActions.navigateTo),
        map((action) => {
          return {
            route: action.route,
            queryParams: action.queryParams,
            onBlankPage: action.onBlankPage,
            external: action.external,
          };
        }),
        tap((navigationData) => {
          if (navigationData && navigationData?.route) {
            if (navigationData.external) {
              const url = navigationData.route;
              if (navigationData.onBlankPage) {
                window.open(url.toString(), '_blank');
              } else {
                window.open(url.toString(), '_self');
              }
            } else {
              if (navigationData.queryParams) {
                if (navigationData?.onBlankPage) {
                  const url = this.router.createUrlTree(navigationData.route, {
                    queryParams: navigationData.queryParams,
                  });
                  window.open(url.toString(), '_blank');
                } else {
                  this.router
                    .navigate(navigationData.route, {
                      queryParams: navigationData.queryParams,
                    })
                    .then();
                }
              } else {
                if (navigationData.onBlankPage) {
                  const url = this.router.createUrlTree(navigationData.route);
                  window.open(url.toString(), '_blank');
                } else {
                  this.router.navigate(navigationData.route).then();
                }
              }
            }
          }
        })
      ),
    { dispatch: false }
  );

  documentTableDownloadRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.documentTableDownloadRequest),
      switchMap(
        (action: {
          request: SubmitDownloadRequest;
          templateDocumentType: TemplateDocumentType;
        }) => {
          if (action.request.ids?.length > 1) {
            return [
              PtCoreActions.documentTableMultipleDownloadRequest({
                request: action.request,
                templateDocumentType: action.templateDocumentType,
              }),
            ];
          } else {
            return [
              PtCoreActions.documentTableSingleDownloadRequest({
                request: action.request,
                templateDocumentType: action.templateDocumentType,
              }),
            ];
          }
        }
      )
    )
  );

  documentTableMultipleDownloadRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PtCoreActions.documentTableMultipleDownloadRequest),
        map(
          (action: {
            request: SubmitDownloadRequest;
            templateDocumentType: TemplateDocumentType;
          }) => {
            this.templateService
              .findDocTemplateChapters(action.templateDocumentType)
              .subscribe((response: ListTemplateChaptersForDownload) => {
                this.runSelectTemplateChapterDialog(response).subscribe(
                  (chapters?: Chapter[]) => {
                    if (chapters) {
                      const request: SubmitDownloadRequest = {
                        downloadType: action.request.downloadType,
                        ids: action.request.ids,
                        chapterSelectors: chapters
                          ? chapters.map((c) => c.selector)
                          : [],
                      };
                      this.downloadService
                        .startDownloadJob(request, action.templateDocumentType)
                        .subscribe(
                          (response: SubmitDownloadResponse) => {
                            this.store.dispatch(
                              PtCoreActions.documentTableDownloadRequestSuccess(
                                {
                                  response,
                                  templateDocumentType:
                                    action.templateDocumentType,
                                }
                              )
                            );
                          },
                          () => {
                            this.store.dispatch(
                              PtCoreActions.documentTableDownloadRequestFailed({
                                request: action.request,
                                templateDocumentType:
                                  action.templateDocumentType,
                              })
                            );
                          }
                        );
                    }
                  }
                );
              });
          }
        )
      ),
    { dispatch: false }
  );

  documentTableSingleDownloadRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PtCoreActions.documentTableSingleDownloadRequest),
        map(
          (action: {
            request: SubmitDownloadRequest;
            templateDocumentType: TemplateDocumentType;
          }) => {
            const service = this.downloadService.getTemplateDocumentTypeService(
              action.templateDocumentType
            );
            if (service && action.request.ids?.length === 1) {
              service
                .getDocumentTemplateChapters(action.request.ids[0])
                .subscribe((response: ListTemplateChapterResponse) => {
                  this.runSelectTemplateChapterDialog(response).subscribe(
                    (chapters?: Chapter[]) => {
                      if (chapters) {
                        const request: SubmitDownloadRequest = {
                          downloadType: action.request.downloadType,
                          ids: action.request.ids,
                          chapterSelectors: chapters
                            ? chapters.map((c) => c.selector)
                            : [],
                        };
                        this.downloadService
                          .startDownloadJob(
                            request,
                            action.templateDocumentType
                          )
                          .subscribe(
                            (response: SubmitDownloadResponse) => {
                              this.store.dispatch(
                                PtCoreActions.documentTableDownloadRequestSuccess(
                                  {
                                    response,
                                    templateDocumentType:
                                      action.templateDocumentType,
                                  }
                                )
                              );
                            },
                            () => {
                              this.store.dispatch(
                                PtCoreActions.documentTableDownloadRequestFailed(
                                  {
                                    request: action.request,
                                    templateDocumentType:
                                      action.templateDocumentType,
                                  }
                                )
                              );
                            }
                          );
                      }
                    }
                  );
                });
            }
          }
        )
      ),
    { dispatch: false }
  );

  documentTableDownloadRequestSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.documentTableDownloadRequestSuccess),
      switchMap(
        (action: {
          response: SubmitDownloadResponse;
          templateDocumentType: TemplateDocumentType;
        }) => {
          return [
            PtCoreActions.openSessionNotifications(),
            PtCoreActions.addSessionNotifications({
              sessionNotification: {
                id: Date.now(),
                name: `${this.getTranslation('LABELS.DOWNLOAD')}: ${
                  action.response.uuid
                }`,
                description: `${this.getTranslation(
                  'LABELS.DOWNLOAD_' +
                    action.templateDocumentType
                      .toUpperCase()
                      .split('-')
                      .join('_')
                )}...`,
                createdAt: Date.now(),
                status: SessionNotificationStatus.RUNNING,
                actionType: SessionInfoType.DOWNLOAD,
                payload: action.response,
              } as SessionNotification,
            }),
          ];
        }
      )
    )
  );

  getDownloadFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.documentTableDownloadRequestFailed),
      switchMap(() => [
        PtCoreActions.propagateNotification({
          notification: {
            type: NotificationType.error,
            title: 'TITLE.DOWNLOAD',
            message: 'MESSAGE.DOWNLOAD_FAILED',
            translated: false,
          } as UiPropagatedNotification,
        }),
      ])
    )
  );

  getDownloadStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.getDownloadStatus),
      switchMap((action) =>
        this.downloadService
          .findAllJobs({
            jobUuids: action.downloadIds,
          })
          .pipe(
            map((download) => {
              return PtCoreActions.getDownloadStatusSuccess({
                download: download.jobs,
              });
            }),
            catchError(() => {
              return of(
                PtCoreActions.getDownloadStatusFailed({
                  downloadIds: action.downloadIds,
                })
              );
            })
          )
      )
    )
  );

  getDownloadStatusSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.getDownloadStatusSuccess),
      withLatestFrom(this.store.select(PtCoreSelectors.sessionNotifications)),
      switchMap(([action, sessionNotifications]) => {
        const data: SessionNotification[] = JSON.parse(
          JSON.stringify(sessionNotifications)
        );
        const jobStatus: JobStatus[] = action.download;
        if (jobStatus && data) {
          data.forEach((sn) => {
            if (sn?.actionType === SessionInfoType.DOWNLOAD) {
              const updateNotification = jobStatus?.find(
                (js) => js.uuid === sn.payload?.uuid
              );
              if (updateNotification) {
                // va sovrascritto nel caso di caricamento fallito ma parzialmente valido
                sn.description = `${
                  updateNotification.completed
                } ${this.completedLabel.toLowerCase()} ${this.ofLabel.toLowerCase()} ${
                  updateNotification.total
                }`;

                switch (updateNotification.status) {
                  case 'COMPLETED':
                    sn.status = SessionNotificationStatus.COMPLETED;
                    break;
                  case 'FAILED':
                    if (updateNotification.completed > 0) {
                      sn.status = SessionNotificationStatus.PARTIAL_COMPLETED;
                      sn.description = `${
                        updateNotification.completed
                      } ${this.completedLabel.toLowerCase()} ${this.andLabel.toLowerCase()} ${
                        updateNotification.total - updateNotification.completed
                      } ${this.failedLabel.toLowerCase()}`;
                    } else {
                      sn.status = SessionNotificationStatus.ERROR;
                    }
                    break;
                  case 'WORKING':
                    sn.status = SessionNotificationStatus.RUNNING;
                    break;
                }
              }
            }
          });
        }
        if (
          data.filter((n) => n.status !== 'COMPLETED' && n.status !== 'ERROR')
            ?.length === 0
        ) {
          return [
            PtCoreActions.updateDownloadSessionNotifications({
              sessionNotifications: data,
            }),
            PtCoreActions.openSessionNotifications(),
          ];
        }
        return [
          PtCoreActions.updateDownloadSessionNotifications({
            sessionNotifications: data,
          }),
        ];
      })
    )
  );

  getDownloadStatusFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.getDownloadStatusFailed),
      withLatestFrom(this.store.select(PtCoreSelectors.sessionNotifications)),
      switchMap(([action, sessionNotifications]) => {
        const data: SessionNotification[] = JSON.parse(
          JSON.stringify(sessionNotifications)
        );
        const jobs: string[] = action.downloadIds;
        if (jobs && data) {
          data.forEach((sn) => {
            if (sn?.actionType === SessionInfoType.DOWNLOAD) {
              if (jobs.includes(sn.payload?.uuid)) {
                sn.status = SessionNotificationStatus.ERROR;
              }
            }
          });
        }
        return [
          PtCoreActions.updateDownloadSessionNotifications({
            sessionNotifications: data,
          }),
        ];
      })
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.login),
      switchMap((action) =>
        this.authService
          .login(
            action.domain,
            action.username,
            action.password,
            action.otpCode,
            action.rcToken
          )
          .pipe(
            map((loginResponse: LoggedUser) => {
              return PtCoreActions.loginSuccess({ loginResponse });
            }),
            catchError(() => {
              return of(
                PtCoreActions.loginFailed({
                  username: action.username,
                  password: action.password,
                })
              );
            })
          )
      )
    )
  );

  loginAsSystem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.loginAsSystem),
      switchMap((action) =>
        this.authService
          .loginAsSystem(
            action.domain,
            action.username,
            action.password,
            action.otpCode,
            action.rcToken
          )
          .pipe(
            map((loginResponse: LoggedUser) => {
              return PtCoreActions.loginAsSystemSuccess({ loginResponse });
            }),
            catchError(() => {
              return of(
                PtCoreActions.loginFailed({
                  username: action.username,
                  password: action.password,
                })
              );
            })
          )
      )
    )
  );

  loginToConsole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.loginToConsole),
      switchMap((action) =>
        this.authService
          .loginToConsole(
            action.username,
            action.password,
            action.otpCode,
            action.rcToken
          )
          .pipe(
            map((loginResponse: LoggedUser) =>
              PtCoreActions.loginToConsoleSuccess({ loginResponse })
            ),
            catchError(() => {
              return of(
                PtCoreActions.loginFailed({
                  username: action.username,
                  password: action.password,
                })
              );
            })
          )
      )
    )
  );

  connectAsUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.connectAsUser),
      withLatestFrom(this.store.select(PtCoreSelectors.loginResponse)),
      switchMap(([action, loggedUser]) =>
        this.usersService.connectAsUser(action.requestBody).pipe(
          map((response: LoggedUser) => {
            const result: LoggedUser = {
              ...loggedUser,
              id: response.id,
              name: response.name,
              email: response.email,
              system: response.system,
              surname: response.surname,
              username: response.username,
              language: response.language,
              permissions: response.permissions,
              enumeratedPermissions: response.enumeratedPermissions,
            };
            return PtCoreActions.connectAsUserSuccess({
              loginResponse: result,
            });
          }),
          catchError(() => {
            return of(PtCoreActions.connectAsUserFailed());
          })
        )
      )
    )
  );

  getLegalEntity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.getLegalEntity),
      switchMap(() =>
        this.legalEntityService.getLegalEntities().pipe(
          map((legalEntity: LegalEntity[]) =>
            PtCoreActions.getLegalEntitySuccess({ legalEntity: legalEntity })
          ),
          catchError(() => of(PtCoreActions.getLegalEntityFailed()))
        )
      )
    )
  );

  loadLoginModules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.loadLoginModules),
      switchMap(() =>
        this.organizationsService.activeLoginModule().pipe(
          map((response: GetLoginModulesResponse) =>
            PtCoreActions.setLoginModulesResponse({
              loginModules: response.loginModules,
            })
          ),
          catchError(() =>
            of(
              PtCoreActions.setLoginModulesResponse({
                loginModules: [],
              })
            )
          )
        )
      )
    )
  );

  getOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.getOrganization),
      switchMap(() =>
        this.organizationsService.getOrganizationSelf().pipe(
          map((organization: Organization) =>
            PtCoreActions.getOrganizationSuccess({ organization })
          ),
          catchError(() => of(PtCoreActions.getOrganizationFailed()))
        )
      )
    )
  );

  getOrganizationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.getOrganizationSuccess),
      switchMap(() => [PtCoreActions.getOrganizationStorageInfo()])
    )
  );

  getOrganizationStorageInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.getOrganizationStorageInfo),
      switchMap(() =>
        this.authService.getOrganizationStorageInfo().pipe(
          map((storageInfo: StorageInfo) =>
            PtCoreActions.getOrganizationStorageInfoSuccess({ storageInfo })
          ),
          catchError(() => of(PtCoreActions.getOrganizationStorageInfoFailed()))
        )
      )
    )
  );

  navigateBack$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PtCoreActions.navigateBack),
        tap(
          (action: { hop: number; queryParams?: Record<string, unknown> }) => {
            const path = window.location.pathname
              .split('/')
              .filter((t) => !!t && t.length > 0);
            if (path.length > 0) {
              const pathWithHop = path.slice(0, -Math.abs(action.hop));
              if (action.queryParams) {
                this.router
                  .navigate(pathWithHop, { queryParams: action.queryParams })
                  .then();
              } else {
                this.router.navigate(pathWithHop).then();
              }
            }
          }
        )
      ),
    { dispatch: false }
  );

  updateOrganizationCss$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PtCoreActions.updateOrganizationCss),
      withLatestFrom(this.store.select(PtCoreSelectors.organization)),
      map(
        ([action, organization]) =>
          ({
            css: action.css,
            logo: organization.logo,
            name: organization.name,
            theme: organization.theme,
            language: organization.language,
            timezone: organization.timezone,
            vatNumber: organization.vatNumber,
            description: organization.description,
            businessName: organization.businessName,
            resellerLogo: organization.resellerLogo,
            storageSpace: organization.storageSpace,
            billingAddress: organization.billingAddress,
            attachmentMaxSize: organization.attachmentMaxSize,
            editableResellerLogo: organization.editableResellerLogo,
          } as UpdateOrganizationRequest)
      ),
      switchMap((organization) =>
        this.authService.updateOrganization(organization).pipe(
          map(() =>
            PtCoreActions.updateOrganizationCssSuccess({
              response: organization,
            })
          ),
          catchError(() => of(PtCoreActions.updateOrganizationCssFail))
        )
      )
    )
  );

  updateOrganizationCssSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PtCoreActions.updateOrganizationCssSuccess),
        tap(() => {
          const notification: UiPropagatedNotification = {
            type: NotificationType.success,
            title: null,
            message: 'MESSAGE.SUCCESSFULLY_SAVED',
            translated: false,
          };
          this.store.dispatch(
            PtCoreActions.propagateNotification({ notification })
          );
        })
      ),
    { dispatch: false }
  );

  notificationTwoFactorAuthentication$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PtCoreActions.notificationTwoFactorAuthentication),
        tap(() => {
          this.modalService.dismissAll();
          const modalRef = this.modalService.open(
            TwoFactorAuthenticationDialogComponent,
            {
              centered: true,
              backdrop: 'static',
              size: 'md',
            }
          );
          modalRef.componentInstance.dialogMode = 'NOTIFICATION';
        })
      ),
    { dispatch: false }
  );

  generateTwoFactorAuthentication$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PtCoreActions.generateTwoFactorAuthentication),
        tap((action) => {
          this.modalService.dismissAll();
          const modalRef = this.modalService.open(
            TwoFactorAuthenticationDialogComponent,
            {
              centered: true,
              backdrop: 'static',
              size: 'lg',
              windowClass: 'md-620',
            }
          );
          modalRef.componentInstance.dialogMode = 'GENERATE';
          modalRef.componentInstance.secretGenerated = action.secretGenerated;
          from(modalRef.result).subscribe((success) => {
            if (success) {
              if (success) {
                this.store.dispatch(
                  PtCoreActions.twoFactorAuthenticationConfirmed({request: success})
                );
              } else {
                this.store.dispatch(
                  PtCoreActions.twoFactorAuthenticationConfirmed({})
                );
              }
            }
          });
        })
      ),
    { dispatch: false }
  );

  resetTwoFactorAuthentication$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PtCoreActions.resetTwoFactorAuthentication),
        tap(({ user }) => {
          this.modalService.dismissAll();

          // Flusso attuale: l'utente resetta la propria 2FA
          const modalRef = this.modalService.open(
            TwoFactorAuthenticationDialogComponent,
            { centered: true, backdrop: 'static', size: 'md' }
          );
          modalRef.componentInstance.dialogMode = 'RESET';

          from(modalRef.result).subscribe((success) => {
            if (success) {
              if (user) {
                this.store.dispatch(
                  PtCoreActions.userTwoFactorAuthenticationConfirmed({ user })
                );
              } else {
                this.store.dispatch(
                  PtCoreActions.resetTwoFactorAuthenticationConfirmed()
                );
              }
            }
          });
        })
      ),
    { dispatch: false }
  );

  setQueryParams$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setQueryParams),
        tap((actions) => {
          this.router
            .navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: actions.queryParams ? actions.queryParams : [],
              queryParamsHandling: actions.handling
                ? actions.handling
                : undefined, //'merge', // remove to replace all query params by provided
            })
            .then();
        })
      ),
    { dispatch: false }
  );

  private getTranslation(string: string): string {
    let label = '';
    this.translate.stream(string).subscribe((translation) => {
      label = translation;
    });
    return label;
  }

  runSelectTemplateChapterDialog = (
    response: ListTemplateChaptersForDownload | ListTemplateChapterResponse
  ) => {
    const modalRef = this.modalService.open(SelectChapterDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: 'md',
    });
    modalRef.componentInstance.title = 'TITLE.DOWNLOAD';
    modalRef.componentInstance.run(response);
    return from(modalRef.result);
  };
}
